/**
 * Phase 19 / Task 19.4 (DEC-325) — pure helpers for the Finance dashboard.
 * No React, so they are tested directly. Date math is calendar-only (never UTC days,
 * DEC-004); money goes through lib/money.ts.
 */

import { formatUsdFromCents } from '../../lib/money';

/** Today's calendar date in `timeZone`, as 'YYYY-MM-DD'. */
export function localToday(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Shift a 'YYYY-MM-DD' calendar date by whole days (date-only math, DST-safe). */
export function shiftDate(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return ymd;
  return new Date(Date.UTC(y, m - 1, d) + days * 86_400_000).toISOString().slice(0, 10);
}

export type RangePreset = 'LAST_30' | 'LAST_90' | 'MONTH_TO_DATE';

/** Inclusive [from, to] for a preset, ending today in `timeZone`. */
export function presetRange(
  preset: RangePreset,
  timeZone: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const to = localToday(timeZone, now);
  if (preset === 'MONTH_TO_DATE') return { from: `${to.slice(0, 8)}01`, to };
  return { from: shiftDate(to, preset === 'LAST_30' ? -29 : -89), to };
}

/** '1234.50' → "1,234.50" without going through a float. */
export function formatBbl(bbl: string): string {
  const neg = bbl.startsWith('-');
  const [whole = '0', frac = '00'] = (neg ? bbl.slice(1) : bbl).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${neg ? '-' : ''}${grouped}.${frac.padEnd(2, '0').slice(0, 2)}`;
}

/** % change → arrow + sign; "no prior data" when there is nothing to compare with. */
export function formatChange(pct: number | null | undefined): {
  readonly text: string;
  readonly direction: 'UP' | 'DOWN' | 'FLAT' | 'NONE';
} {
  if (pct === null || pct === undefined) return { text: 'no prior data', direction: 'NONE' };
  if (pct === 0) return { text: '0.0%', direction: 'FLAT' };
  return pct > 0
    ? { text: `▲ ${pct.toFixed(1)}%`, direction: 'UP' }
    : { text: `▼ ${Math.abs(pct).toFixed(1)}%`, direction: 'DOWN' };
}

/** 87.5 → "87.5%"; null → "—". */
export function formatPct(pct: number | null): string {
  return pct === null ? '—' : `${pct.toFixed(1)}%`;
}

/** Cents → "$1,234.56"; absent (stripped for this role) or null → "—". */
export function formatCents(cents: number | null | undefined): string {
  return typeof cents === 'number' ? formatUsdFromCents(cents) : '—';
}

/** 1 → "1 invoice", 2 → "2 invoices". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

/** 'YYYY-MM-DD' → "Mar 7" (a calendar date: formatted in UTC so it never shifts a day). */
export function formatShortDay(ymd: string): string {
  const t = Date.parse(`${ymd}T00:00:00Z`);
  if (Number.isNaN(t)) return ymd;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(t));
}
